import React, { useEffect, useMemo, useRef, useState } from "react";
import { CheckCircle, XCircle, Clock, UserCheck } from "lucide-react";
import Modal from "../../../components/ui/Modal";

interface SubTimeSlot {
  time: string;
  isAvailable: boolean;
}

interface TimeSlotDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  day: string;
  timeSlot: {
    period: string;
    startTime: string;
    endTime: string;
  };
  subTimeSlots: SubTimeSlot[];
  onToggleSubSlot?: (time: string) => void;
  onSelectAll?: () => void;
  onClearAll?: () => void;
  disabled?: boolean;
  readOnly?: boolean;
  onPrivateAppointment?: () => void;
  onConfirm?: (selectedTimes: string[]) => void | Promise<void>;
}

const TimeSlotDetailModal: React.FC<TimeSlotDetailModalProps> = ({
  isOpen,
  onClose,
  day,
  timeSlot,
  subTimeSlots,
  onToggleSubSlot,
  onSelectAll,
  onClearAll,
  disabled = false,
  readOnly = false,
  onPrivateAppointment,
  onConfirm,
}) => {
  const [localSlots, setLocalSlots] = useState<SubTimeSlot[]>(subTimeSlots);
  const [saving, setSaving] = useState(false);
  const initialHasAvailableRef = useRef<boolean>(false);

  useEffect(() => {
    if (isOpen) {
      setLocalSlots(subTimeSlots);
      initialHasAvailableRef.current = subTimeSlots.some((s) => s.isAvailable);
    }
  }, [isOpen, subTimeSlots]);

  const availableCount = useMemo(() => localSlots.filter((s) => s.isAvailable).length, [localSlots]);

  const handleToggleLocal = (time: string) => {
    if (disabled) return;

    if (readOnly && onToggleSubSlot) {
      onToggleSubSlot(time);
      return;
    }

    if (!readOnly) {
      setLocalSlots((prev) => prev.map((s) => (s.time === time ? { ...s, isAvailable: !s.isAvailable } : s)));
    }
  };

  const handleSelectAllLocal = () => {
    if (disabled || readOnly) return;
    setLocalSlots((prev) => prev.map((s) => ({ ...s, isAvailable: true })));
  };

  const handleClearAllLocal = () => {
    if (disabled || readOnly) return;
    setLocalSlots((prev) => prev.map((s) => ({ ...s, isAvailable: false })));
  };

  const handleSave = async () => {
    if (!onConfirm) {
      onClose();
      return;
    }
    try {
      setSaving(true);
      const selectedTimes = localSlots.filter((s) => s.isAvailable).map((s) => s.time);
      await onConfirm(selectedTimes);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const handlePrivateAppointmentClick = async () => {
    if (!onConfirm || !onPrivateAppointment) return;

    try {
      setSaving(true);
      const selectedTimes = localSlots.filter((s) => s.isAvailable).map((s) => s.time);
      await onConfirm(selectedTimes);
      onPrivateAppointment();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" title={`${day} - ${timeSlot.period} (${timeSlot.startTime} - ${timeSlot.endTime})`}>
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-4">
          {readOnly && <p className="text-gray-600 text-sm">Müsait olan zaman dilimlerini seçin:</p>}
          {!readOnly && onPrivateAppointment && (
            <button
              onClick={handlePrivateAppointmentClick}
              disabled={!initialHasAvailableRef.current || saving}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors flex items-center gap-1.5 ${
                initialHasAvailableRef.current && !saving
                  ? "bg-purple-600 hover:bg-purple-700 text-white"
                  : "bg-gray-200 text-gray-500 cursor-not-allowed"
              }`}>
              <UserCheck size={14} />
              Özel Randevu
            </button>
          )}
          {!readOnly && (
            <div className="flex items-center gap-2 ml-auto">
              <button
                onClick={handleSelectAllLocal}
                disabled={disabled}
                className="px-3 py-1.5 text-xs font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5">
                <CheckCircle size={14} />
                <span>Tümünü Seç</span>
              </button>
              <button
                onClick={handleClearAllLocal}
                disabled={disabled}
                className="px-3 py-1.5 text-xs font-medium bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5">
                <XCircle size={14} />
                <span>Tümünü Kaldır</span>
              </button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {(readOnly ? localSlots.filter((slot) => slot.isAvailable) : localSlots).map((slot) => (
            <button
              key={slot.time}
              onClick={() => handleToggleLocal(slot.time)}
              disabled={disabled}
              className={`p-4 rounded-lg border-2 transition-all duration-200 flex items-center ${
                readOnly ? "justify-center" : "justify-between"
              } ${disabled ? "opacity-50 cursor-not-allowed" : "hover:scale-105 active:scale-95 cursor-pointer hover:shadow-md"} ${
                readOnly
                  ? "bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100"
                  : slot.isAvailable
                  ? "bg-green-50 border-green-200 text-green-700 hover:bg-green-100 shadow-sm"
                  : "bg-red-50 border-red-300 text-red-700 hover:bg-red-100 shadow-sm"
              }`}>
              <span className={`${readOnly ? "font-semibold" : "font-medium"} text-lg`}>{slot.time}</span>
              {!readOnly &&
                (slot.isAvailable ? (
                  <CheckCircle size={20} className="drop-shadow-sm" />
                ) : (
                  <XCircle size={20} className="drop-shadow-sm" />
                ))}
            </button>
          ))}
        </div>

        {readOnly && localSlots.filter((slot) => slot.isAvailable).length === 0 && (
          <div className="text-center py-8">
            <XCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 text-sm">Bu saat aralığında müsait zaman yok</p>
          </div>
        )}

        {!readOnly && (
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              İptal
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              {saving ? "Kaydediliyor..." : "Kaydet"}
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default TimeSlotDetailModal;
